// src/services/sse/types.ts
/**
 * SSE Event Types - Mirrors backend Pydantic schemas
 * Keep in sync with job_mobile/schemas/sse.py
 */

// ============================================================================
// Event Type Enums (match backend SSEEventType)
// ============================================================================

export enum SSEEventType {
  BALANCE_UPDATE = 'balance_update',
  BALANCE_INITIAL = 'balance_initial',
  NOTIFICATION_NEW = 'notification_new',
  NOTIFICATION_READ = 'notification_read',
  JOB_SEARCH_STATUS = 'job_search_status',
  JOB_SEARCH_PROGRESS = 'job_search_progress',
  JOB_SEARCH_COMPLETE = 'job_search_complete',
  RESUME_STATUS = 'resume_status',
  RESUME_PROGRESS = 'resume_progress',
  RESUME_COMPLETE = 'resume_complete',
  PREFERENCES_EXTRACTED = 'preferences_extracted',
  PREFERENCES_UPDATED = 'preferences_updated',
  PREFERENCES_SHOW_ONBOARDING = 'preferences_show_onboarding',
  HEARTBEAT = 'heartbeat',
  ERROR = 'error',
}

// ============================================================================
// Balance Change Type Enum (match backend BalanceChangeType)
// ============================================================================

export enum BalanceChangeType {
  INITIAL = 'initial',
  DEDUCT = 'deduct',
  ADD = 'add',
  UPDATE = 'update',
}

// ============================================================================
// Base Event (match backend BaseSSEEvent)
// ============================================================================

export interface BaseSSEEvent {
  type: SSEEventType;
  timestamp: string; // ISO 8601 datetime
  message?: string | null;
}

// ============================================================================
// Balance Update Event (match backend BalanceUpdateEvent)
// ============================================================================

export interface BalanceUpdateEvent extends BaseSSEEvent {
  type: SSEEventType.BALANCE_UPDATE | SSEEventType.BALANCE_INITIAL;
  user_id: string;
  balance: number;
  balance_display: string;
  updated_on: string; // ISO 8601 datetime
  change_type: BalanceChangeType;
  transaction_id?: string | null;
}

// ============================================================================
// Error Event (match backend ErrorEvent)
// ============================================================================

export interface ErrorEvent extends BaseSSEEvent {
  type: SSEEventType.ERROR;
  message: string; // Required for errors
}

// ============================================================================
// Heartbeat Event (match backend HeartbeatEvent)
// ============================================================================

export interface HeartbeatEvent extends BaseSSEEvent {
  type: SSEEventType.HEARTBEAT;
}

// ============================================================================
// Notification Events (Future - match backend NotificationEvent)
// ============================================================================

export interface NotificationEvent extends BaseSSEEvent {
  type: SSEEventType.NOTIFICATION_NEW | SSEEventType.NOTIFICATION_READ;
  user_id: string;
  notification_id: string;
}

// ============================================================================
// Job Search Events (Future - match backend JobSearchStatusEvent)
// ============================================================================

export interface JobSearchStatusEvent extends BaseSSEEvent {
  type: SSEEventType.JOB_SEARCH_STATUS | SSEEventType.JOB_SEARCH_PROGRESS | SSEEventType.JOB_SEARCH_COMPLETE;
  task_id: string;
  status: string;
  progress?: number | null; // 0-100
  total_jobs?: number | null;
}

// ============================================================================
// Resume Analysis Events (Future - match backend ResumeStatusEvent)
// ============================================================================

export interface ResumeStatusEvent extends BaseSSEEvent {
  type: SSEEventType.RESUME_STATUS | SSEEventType.RESUME_PROGRESS | SSEEventType.RESUME_COMPLETE;
  resume_id: string;
  status: string;
  progress?: number | null; // 0-100
}

// ============================================================================
// Preference Events (match backend PreferenceEvent)
// ============================================================================

export interface PreferenceEvent extends BaseSSEEvent {
  type: SSEEventType.PREFERENCES_EXTRACTED | SSEEventType.PREFERENCES_UPDATED | SSEEventType.PREFERENCES_SHOW_ONBOARDING;
  user_id: string;
  overall_confidence?: number | null;
  should_show_onboarding?: boolean | null;
  low_confidence_sections?: string[] | null;
}

// ============================================================================
// Union Type for All Events
// ============================================================================

export type SSEEvent =
  | BalanceUpdateEvent
  | NotificationEvent
  | JobSearchStatusEvent
  | ResumeStatusEvent
  | PreferenceEvent
  | HeartbeatEvent
  | ErrorEvent;

// ============================================================================
// Hook Return Types
// ============================================================================

export interface UseSSEReturn<T extends BaseSSEEvent> {
  data: T | null;
  loading: boolean;
  error: string | null;
  connected: boolean;
  retry?: () => void;
}

export interface SSEOptions {
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
  onMessage?: (data: BaseSSEEvent) => void;
  onError?: (error: Event) => void;
  onConnect?: () => void;
  debug?: boolean;
}

// ============================================================================
// Error Types
// ============================================================================

export enum SSEErrorType {
  AUTHENTICATION = 'authentication',
  NETWORK = 'network',
  PARSE = 'parse',
  SERVER = 'server',
  UNKNOWN = 'unknown',
}

export interface SSEError {
  type: SSEErrorType;
  message: string;
  retryable: boolean;
  originalError?: Error;
}

